import React, { useState, useEffect } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Space, Button, Input, Form, Modal, Tooltip, Select } from 'antd';
import { NotificationInstance } from 'antd/es/notification/interface';
import { PlusOutlined } from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';

import { exception } from '@extensions/notification';

import { useAppDispatch } from '@store/hooks';
import { userLoaded } from '@store/actions';

import { ITag } from '@entities/tag';

dayjs.extend(utc);

interface ITagProps extends IBaseComponentProps {
    initialTags: Array<string>;
    isEditMode: boolean;
    api: NotificationInstance;
    onSelect: (value: Array<string> | undefined) => void;
}

const Tag = (props: ITagProps) => {
    const { initialTags, api, isEditMode, onSelect, serverFetch } = props;

    const [tagForm] = Form.useForm();

    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();

    const [baseTags, setBaseTags] = useState<Array<ITag>>([]);
    const [tags, setTags] = useState<Array<ITag>>([]);
    const [currentTag, setCurrentTag] = useState<ITag>();
    const [tagOpen, setTagOpen] = useState<boolean>(false);

    const [refreshRequired, setRefreshRequired] = useState<boolean>(true);

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);

        setBaseTags([]);

        serverFetch(`tags`, { method: 'GET' })
            .then((data: Array<ITag>) => {
                setBaseTags(data);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения тегов', ex, () => d(userLoaded(undefined)));
            });
    }, [refreshRequired]);

    useEffect(() => {
        setTags(baseTags);
    }, [baseTags]);

    /*     useEffect(() => {
        onSetCurrent(tag);
    }, [tags, baseTags]); */

    const onAdd = (entity: ITag) => {
        serverFetch(`tags`, { method: 'POST', bodyData: entity })
            .then((data: ITag) => {
                tagForm.resetFields();
                setTagOpen(false);
                setRefreshRequired(true);

                let baseTagsCopy = [...baseTags];
                baseTagsCopy.push(data);
                setBaseTags(baseTagsCopy);

                let selectedTagsCopy = [...initialTags];
                data.name && selectedTagsCopy.push(data.name);

                onSelect(selectedTagsCopy);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка добавления тега', ex, () => d(userLoaded(undefined)));
            });
    };

    return (
        <>
            <Space.Compact style={{ width: '100%' }}>
                <Select
                    mode='multiple'
                    disabled={!isEditMode}
                    style={{ width: isEditMode ? 'calc(100% - 30px)' : '100%' }}
                    value={initialTags}
                    allowClear={true}
                    showSearch
                    onChange={(e) => {
                        if (!e) {
                            onSelect(undefined);
                            return;
                        }

                        onSelect(e);
                    }}
                    optionFilterProp='children'
                    filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                    filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                    options={tags.map((c) => {
                        return { value: c.name, label: `#${c.name}` };
                    })}
                />
                {isEditMode && (
                    <Tooltip title='Добавить'>
                        <Button icon={<PlusOutlined />} onClick={() => setTagOpen(true)} hidden={!currentTag || !isEditMode} />
                    </Tooltip>
                )}
            </Space.Compact>

            <Modal
                title='Добавить тег'
                open={tagOpen}
                okText='ОК'
                onOk={() => tagForm.submit()}
                onCancel={() => {
                    tagForm.resetFields();

                    setTagOpen(false);
                }}
            >
                <Form colon={false} labelCol={{ span: 5 }} wrapperCol={{ span: 17 }} onFinish={onAdd} form={tagForm}>
                    <Form.Item
                        required
                        label='Тег'
                        name='name'
                        rules={[
                            { required: true, message: 'Укажите название тега' },
                            {
                                validator: async (_, value) => {
                                    let isExist = await serverFetch(`tags/exist/${value}`, { method: 'GET' })
                                        .then((data: any) => {
                                            return data;
                                        })
                                        .catch((ex: any) => {
                                            exception(api, 'Ошибка проверки тега', ex, () => d(userLoaded(undefined)));
                                        });

                                    if (isExist) return Promise.reject('Такой тег уже существует');
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input autoFocus prefix='#' />
                    </Form.Item>
                </Form>
            </Modal>

            {modalContextHolder}
        </>
    );
};

export default Tag;
